import type { BenSpecQuestionRow } from "../types";

/**
 * Split one CSV record (single line) into fields. Supports quoted cells with commas.
 */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let i = 0;
  let inQuotes = false;
  while (i < line.length) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cur += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ",") {
      out.push(cur.trim());
      cur = "";
      i += 1;
      continue;
    }
    cur += c;
    i += 1;
  }
  out.push(cur.trim());
  return out;
}

export type ParseBenSpecCsvOk = { ok: true; rows: BenSpecQuestionRow[] };
export type ParseBenSpecCsvErr = { ok: false; error: string };
export type ParseBenSpecCsvResult = ParseBenSpecCsvOk | ParseBenSpecCsvErr;

function newImportId(lineIndex: number): string {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${lineIndex}`;
  return `ben-spec-import-${suffix}`;
}

/**
 * Parse a user CSV with either flat or CW hierarchy-aware headers.
 */
export function parseBenSpecQuestionsCsv(text: string): ParseBenSpecCsvResult {
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { ok: false, error: "Need a header row and at least one data row." };
  }
  const header = splitCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const idxSection = header.indexOf("section");
  const idxSubSection = header.indexOf("sub section");
  const idxSubSubSection = header.indexOf("sub sub section");
  const idxGuidance = header.indexOf("section guidance");
  const idxLabel = header.findIndex(
    (h) =>
      h === "question label" ||
      h === "questionlabel" ||
      h === "question summary" ||
      h === "questionsummary"
  );
  const idxQuestion = header.indexOf("question");
  if (idxSection < 0 || idxLabel < 0 || idxQuestion < 0) {
    return {
      ok: false,
      error: "CSV must include columns: Section, Question Label, Question.",
    };
  }
  const rows: BenSpecQuestionRow[] = [];
  for (let li = 1; li < lines.length; li++) {
    const fields = splitCsvLine(lines[li]);
    if (fields.every((f) => f === "")) {
      continue;
    }
    const section = fields[idxSection] ?? "";
    const subSection = idxSubSection >= 0 ? (fields[idxSubSection] ?? "") : "";
    const subSubSection = idxSubSubSection >= 0 ? (fields[idxSubSubSection] ?? "") : "";
    const questionLabel = fields[idxLabel] ?? "";
    const question = fields[idxQuestion] ?? "";
    const guidance = idxGuidance >= 0 ? (fields[idxGuidance] ?? "") : "";
    if (!section.trim() && !questionLabel.trim() && !question.trim()) {
      continue;
    }
    const headingPath = buildHeadingPath(section, subSection, subSubSection);
    rows.push({
      id: newImportId(li),
      section: section.trim(),
      subSection: subSection.trim(),
      subSubSection: subSubSection.trim(),
      questionLabel: questionLabel.trim(),
      question: question.trim(),
      headingPath,
      guidance: guidance.trim() || null,
    });
  }
  if (rows.length === 0) {
    return { ok: false, error: "No data rows found after the header." };
  }
  return { ok: true, rows };
}

/**
 * Build a report grouping path from the hierarchy fields.
 *
 * Single source of truth so the CSV importer, the questions editor, and job
 * submission all derive `headingPath` identically: trim + collapse internal
 * whitespace, drop empty levels, and remove adjacent duplicates. Always
 * rebuild from the current fields rather than trusting a cached path, which
 * can go stale after the user edits section/sub-section/sub-sub-section.
 */
export function buildHeadingPath(
  section: string,
  subSection: string,
  subSubSection: string
): string[] {
  const values = [section, subSection, subSubSection]
    .map((value) => value.trim().replace(/\s+/g, " "))
    .filter((value) => value.length > 0);
  return values.filter((value, index) => index === 0 || value !== values[index - 1]);
}

function escapeCsvField(s: string): string {
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** UTF-8 BOM + hierarchy-aware ben spec CSV header. */
export function benSpecQuestionsToCsv(rows: BenSpecQuestionRow[]): string {
  const header = "Section,Sub Section,Sub Sub section,Question Label,Question,Section Guidance";
  const body = rows
    .map((r) =>
      [
        escapeCsvField(r.section),
        escapeCsvField(r.subSection || ""),
        escapeCsvField(r.subSubSection || ""),
        escapeCsvField(r.questionLabel),
        escapeCsvField(r.question),
        escapeCsvField(r.guidance || ""),
      ].join(",")
    )
    .join("\n");
  return `\ufeff${header}\n${body}\n`;
}
